import { useMemo, useState, type FormEvent } from 'react';
import { Check } from 'lucide-react';
import TurnstileWidget from './TurnstileWidget';
import ConsentBlock from './ConsentBlock';
import { submitCapture } from '../../api/forms';
import {
  ADULT_GROUP_FROM_AGE,
  CHILD_AGE_GROUPS,
  agesOf,
  chooseCourse,
  formatAgeRange,
  groupLabel,
  isOfferedTo,
  sameGroup,
  type AgeGroup,
} from '../../lib/ageGroups';
import { config } from '../../lib/config';
import { CONSENT_TEXT_VERSION } from '../../lib/constants';
import { LAENDER, flagge, istPlausibel, istWaehlbar, zuE164 } from '../../lib/telefon';
import type { FormOption, LeadInput } from '../../types/forms';

interface Props {
  options: FormOption[];
  // option = null → keine konkrete Sportart („Ich bin mir nicht sicher") →
  // keine Terminbuchung, nur Rückruf. ageGroup = Altersgruppe des Kindes
  // („Für mein Kind"), sonst null. parentsMustCome = das Kind kommt für einen
  // Erwachsenenkurs in Frage und ist noch nicht volljährig.
  onCaptured: (
    leadId: string,
    option: FormOption | null,
    ageGroup: AgeGroup | null,
    parentsMustCome: boolean,
  ) => void;
}

const UNSURE = 'unsure';
const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

/** „9–11 Jahre" bzw. „ab 12 Jahren" — plus „Erwachsenenkurs", wo einer gemeint ist. */
const courseMeta = (o: FormOption): string =>
  [formatAgeRange(o.age_min, o.age_max), o.for_adults === true ? 'Erwachsenenkurs' : '']
    .filter(Boolean)
    .join(' · ');

interface CoursePickProps {
  option: FormOption;
  alternative: FormOption | null;
  useAlternative: boolean;
  onPickAlternative: (useAlternative: boolean) => void;
}

// Bei Kindern zeigen wir den Kurs, der hinter der Sportart steckt — mit seinem
// Altersbereich. So sehen Eltern vor dem Absenden, wofür ihr Kind angemeldet wird.
// Passen zum Alter ein Kinder- und ein Erwachsenenkurs (KBF Kids bis 13, KBF
// Erwachsene ab 12), dürfen die Eltern wählen; der Kinderkurs ist vorausgewählt.
function CoursePick({ option, alternative, useAlternative, onPickAlternative }: CoursePickProps) {
  if (!alternative) {
    const meta = courseMeta(option);
    return (
      <p className="rf-hint rf-hint--info">
        Passender Kurs: <strong>{option.course_name}</strong>
        {meta && ` · ${meta}`}
      </p>
    );
  }
  return (
    <fieldset className="rf-radios">
      <legend className="rf-hint rf-hint--info">
        Zwei Kurse passen zum Alter – welchen möchtet ihr ausprobieren?
      </legend>
      {[option, alternative].map((o, i) => {
        const isAlternative = i === 1;
        const checked = isAlternative === useAlternative;
        return (
          <label key={o.course_id} className={`rf-radio ${checked ? 'rf-radio--on' : ''}`}>
            <input
              type="radio"
              name="rf-course"
              checked={checked}
              onChange={() => onPickAlternative(isAlternative)}
            />
            <span className="rf-radio__text">
              <strong>{o.course_name}</strong>
              <span className="rf-radio__meta">{courseMeta(o)}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export default function LeadForm({ options, onCaptured }: Props) {
  const [isForChild, setIsForChild] = useState(false);
  // Altersgruppe des Kindes — wird bei „Für mein Kind" VOR den Kursen abgefragt
  // und filtert sie (siehe lib/ageGroups.ts).
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [childName, setChildName] = useState('');
  // Deutschland ist voreingestellt; tippt jemand eine eigene Landesvorwahl
  // ins Feld, gewinnt die (siehe `zuE164`).
  const [landCode, setLandCode] = useState('DE');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [schoolId, setSchoolId] = useState('');
  // categoryId: '' | UNSURE | <Kategorie-UUID> (grobe Hauptsportart)
  const [categoryId, setCategoryId] = useState('');
  // Das genaue Alter des Kindes — nur gefragt, wo die Altersgruppe allein nicht
  // reicht, weil kein Kurs der Sportart sie ganz abdeckt (siehe `choice`).
  const [exactAge, setExactAge] = useState<number | null>(null);
  // Passen ein Kinder- und ein Erwachsenenkurs, ist der Kinderkurs vorgewählt; das
  // hier ist true, wenn die Eltern stattdessen den Erwachsenenkurs genommen haben.
  const [altChosen, setAltChosen] = useState(false);
  const [token, setToken] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Distinct schools.
  const schools = useMemo(() => {
    const map = new Map<string, FormOption>();
    for (const o of options) if (!map.has(o.school_id)) map.set(o.school_id, o);
    return Array.from(map.values());
  }, [options]);

  // Optionen am gewählten Standort, gefiltert nach Selbst-/Kind-Anmeldung.
  // Bei Kindern zählen nur Kurse, die für die gewählte Altersgruppe in Frage
  // kommen (Überschneidung; Erwachsenenkurse nur, wenn das CRM ein passendes
  // Mindestalter nennt — siehe `isOfferedTo`) — solange keine gewählt ist,
  // gibt es keine.
  const scoped = useMemo(
    () =>
      options.filter(
        (o) =>
          o.school_id === schoolId &&
          (isForChild ? ageGroup !== null && isOfferedTo(o, ageGroup) : o.for_adults === true),
      ),
    [options, schoolId, isForChild, ageGroup],
  );

  // Grobe Hauptsportarten (CRM-Kategorien) am Standort.
  const categories = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of scoped) {
      if (o.category_id && !m.has(o.category_id)) m.set(o.category_id, o.category_name ?? 'Sportart');
    }
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [scoped]);

  // Erst wenn feststeht, wofür (Kind: Altersgruppe) und wo, lässt sich sagen,
  // was zur Wahl steht.
  const ready = !!schoolId && (!isForChild || ageGroup !== null);

  // Bleibt genau eine Sportart übrig (z. B. Fit Kids bei 2–3 Jahren), gibt es
  // nichts zu entscheiden: Sie ist gesetzt, und „Ich bin mir nicht sicher"
  // entfällt — sonst könnten Eltern die einzig passende Sportart abwählen.
  // Bleibt gar nichts übrig, geht es direkt in den Rückruf.
  const onlyCategory = ready && categories.length === 1 ? categories[0] : null;
  const noOffer = ready && categories.length === 0;
  const activeCategoryId = noOffer ? UNSURE : onlyCategory ? onlyCategory.id : categoryId;
  const activeCategoryName = categories.find((c) => c.id === activeCategoryId)?.name ?? '';

  // Welcher Kurs zur Sportart und Altersgruppe gehört — oder ob dafür das genaue
  // Alter gebraucht wird (siehe lib/ageGroups.ts). Das Mindestalter der
  // Erwachsenenkurse im CRM entscheidet, ob ein Kind hinein darf: „KBF Erwachsene
  // ab 12" steht schon 12-Jährigen offen, und endet der Kinderkurs vor 15 (MMA bis
  // 14), weicht die Auswahl für 15-Jährige auf den Erwachsenenkurs aus.
  const choice = useMemo(() => {
    if (!activeCategoryId || activeCategoryId === UNSURE) return null;
    const adultCourses = isForChild
      ? options.filter(
          (o) =>
            o.school_id === schoolId &&
            o.category_id === activeCategoryId &&
            o.for_adults === true &&
            o.age_min != null,
        )
      : [];
    return chooseCourse(
      scoped.filter((o) => o.category_id === activeCategoryId),
      isForChild ? ageGroup : null,
      exactAge,
      adultCourses,
    );
  }, [options, schoolId, scoped, activeCategoryId, isForChild, ageGroup, exactAge]);

  const selectedOption: FormOption | null =
    choice?.kind === 'auto' ? (altChosen && choice.alternative ? choice.alternative : choice.option) : null;
  const courseId = selectedOption?.course_id ?? '';
  // Das genaue Alter wird nur abgefragt (und gilt nur), wo es den Kurs bestimmt.
  const askAge = isForChild && ageGroup !== null && (choice?.kind === 'needs-age' || exactAge !== null);

  // Erwachsenenkurse für Kinder — und jedes Kind ab 15: noch nicht volljährig,
  // also kommen die Eltern mit, die unterschreiben den Vertrag.
  const parentsMustCome =
    isForChild &&
    (ageGroup?.adults === true ||
      selectedOption?.for_adults === true ||
      (exactAge ?? 0) >= ADULT_GROUP_FROM_AGE);

  // Eine Fehlermeldung verschwindet, sobald die betroffene Auswahl getroffen ist
  // — sonst steht „Bitte wähle die Altersgruppe" noch da, obwohl schon gewählt wurde.
  const clearErrors = (...keys: string[]) =>
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.includes(k))));

  const chooseFor = (child: boolean) => {
    // Erneutes Tippen auf die schon gewählte Seite darf nichts zurücksetzen.
    if (child === isForChild) return;
    setIsForChild(child);
    setAgeGroup(null);
    setCategoryId('');
    setExactAge(null);
    setAltChosen(false);
    clearErrors('ageGroup', 'category', 'exactAge');
  };

  // Jede Änderung davor setzt die Kurswahl zurück: Ob es einen zweiten Kurs zur
  // Auswahl gibt, hängt von Gruppe, Standort, Sportart und Alter ab.
  const onAgeGroupChange = (group: AgeGroup) => {
    setAgeGroup(group);
    setCategoryId('');
    setExactAge(null);
    setAltChosen(false);
    clearErrors('ageGroup', 'category', 'exactAge');
  };

  const onSchoolChange = (id: string) => {
    setSchoolId(id);
    setCategoryId('');
    setExactAge(null);
    setAltChosen(false);
    clearErrors('school', 'category', 'exactAge');
  };

  const onCategoryChange = (value: string) => {
    setCategoryId(value);
    setExactAge(null);
    setAltChosen(false);
    clearErrors('category', 'exactAge');
  };

  const onExactAgeChange = (age: number) => {
    setExactAge(age);
    setAltChosen(false);
    clearErrors('exactAge');
  };

  const vorwahl = LAENDER.find((l) => l.code === landCode)?.vorwahl ?? '49';
  const telefon = zuE164(phone, vorwahl);
  // Erst meckern, wenn überhaupt etwas dasteht — nicht schon beim ersten Zeichen.
  const telefonUnklar = phone.trim().length >= 4 && !istPlausibel(telefon);

  const validate = (): boolean => {
    const e: Record<string, boolean> = {};
    if (!firstName.trim()) e.firstName = true;
    if (!lastName.trim()) e.lastName = true;
    if (isForChild && !childName.trim()) e.childName = true;
    if (!istWaehlbar(telefon)) e.phone = true;
    if (!emailOk(email)) e.email = true;
    if (isForChild && !ageGroup) e.ageGroup = true;
    if (!schoolId) e.school = true;
    // Sportart ODER „nicht sicher" wählen (Ausnahme: nur eine Sportart zur Wahl
    // oder gar keine — dann ist das schon entschieden).
    if (ready && !activeCategoryId) e.category = true;
    // Entscheidet das genaue Alter über den Kurs, muss es gewählt sein.
    if (choice?.kind === 'needs-age') e.exactAge = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    if (!token && !config.isTest) {
      setSubmitError('Bitte bestätige kurz den Bot-Schutz.');
      return;
    }

    const lead: LeadInput = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      age: null,
      is_for_child: isForChild,
      child_name: isForChild ? childName.trim() : null,
      // Das genaue Alter kennen wir nur dort, wo es den Kurs bestimmt hat (sonst
      // reicht die Altersgruppe, in der das Kind zum Zeitpunkt des Probetrainings
      // sein soll).
      child_age: isForChild ? exactAge : null,
      child_age_min: isForChild && ageGroup ? ageGroup.min : null,
      child_age_max: isForChild && ageGroup ? ageGroup.max : null,
      email: email.trim(),
      // Fertig zusammengesetzte Nummer in internationaler Schreibweise. Die
      // Länderauswahl ist dabei nur ein Hinweis: steht im Feld schon „+49",
      // wird sie nicht ein zweites Mal davorgesetzt. Der Server prüft noch
      // einmal nach.
      phone: telefon,
      school_id: schoolId,
      course_id: courseId || null,
      consent_contact: true, // Einwilligung erfolgt mit dem Absenden (Hinweis)
      consent_text_version: CONSENT_TEXT_VERSION,
      source: config.isTest ? 'wordpress-iframe-test' : 'wordpress-iframe',
      // Werbekanal-Herkunft (utm_*) unsichtbar mitgeben, sofern vorhanden.
      ...config.utm,
    };

    setSubmitting(true);
    try {
      const { lead_id } = await submitCapture(lead, token);
      onCaptured(lead_id, selectedOption, isForChild ? ageGroup : null, parentsMustCome);
    } catch (e) {
      setSubmitError((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="rf-eyebrow">Probetraining</div>
      <h1 className="rf-title">
        Sichere dir den <span className="rf-accent">nächsten freien Platz</span> für ein{' '}
        <span className="rf-accent">kostenloses</span> und{' '}
        <span className="rf-accent">unverbindliches</span> Probetraining!
      </h1>

      <form className="rf-card" onSubmit={handleSubmit} noValidate>
        {config.isTest && (
          <div className="rf-alert rf-alert--test">
            Test-Modus aktiv – es werden keine echten Anrufe oder Buchungen ausgelöst.
          </div>
        )}

        <div className="rf-field">
          <label className="rf-label">Für wen ist das Probetraining?</label>
          <div className="seg-control" style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className={`btn ${!isForChild ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={() => chooseFor(false)}
            >
              Für mich
            </button>
            <button
              type="button"
              className={`btn ${isForChild ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={() => chooseFor(true)}
            >
              Für mein Kind
            </button>
          </div>
        </div>

        {/* Bei Kindern zuerst die Altersgruppe: sie bestimmt, welche Kurse weiter
            unten überhaupt zur Wahl stehen. Knöpfe statt Dropdown — die fünf
            Gruppen sind auf einen Blick erfassbar und nicht vorausgewählt. */}
        {isForChild && (
          <div className="rf-field">
            <label className="rf-label" id="rf-agegroup-label">
              Wie alt ist dein Kind?<span className="rf-req">*</span>
            </label>
            <div
              className={`rf-agegroups ${errors.ageGroup ? 'rf-agegroups--error' : ''}`}
              role="group"
              aria-labelledby="rf-agegroup-label"
            >
              {CHILD_AGE_GROUPS.map((g) => {
                const active = sameGroup(ageGroup, g);
                return (
                  <button
                    key={g.min}
                    type="button"
                    aria-pressed={active}
                    className={`btn rf-agebtn ${active ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => onAgeGroupChange(g)}
                  >
                    <span className="rf-agebtn__num">{groupLabel(g)}</span>
                    <span className="rf-agebtn__unit">Jahre</span>
                  </button>
                );
              })}
            </div>
            {errors.ageGroup ? (
              <p className="rf-error">Bitte wähle die Altersgruppe deines Kindes.</p>
            ) : (
              <p className="rf-hint rf-hint--info">
                Wähle die Altersgruppe, in der dein Kind{' '}
                <strong>zum Zeitpunkt des Probetrainings</strong> ist – danach zeigen wir dir
                nur die passenden Kurse.
              </p>
            )}
            {/* Die 15 steht mit Absicht in zwei Gruppen (siehe lib/ageGroups.ts). */}
            {ageGroup && ageGroup.max >= ADULT_GROUP_FROM_AGE && (
              <p className="rf-hint rf-hint--info">
                Mit 15 kann dein Kind noch in die Teenkurse (12–15) oder schon in die
                Erwachsenenkurse (15–17) – such dir die Gruppe aus, die besser passt.
              </p>
            )}
          </div>
        )}

        <div className="rf-row">
          <div className="rf-field">
            <label className="rf-label">Vorname<span className="rf-req">*</span></label>
            <input
              className={`rf-input ${errors.firstName ? 'rf-input--error' : ''}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={isForChild ? 'Dein Vorname' : 'Vorname'}
              autoComplete="given-name"
            />
          </div>
          <div className="rf-field">
            <label className="rf-label">Nachname<span className="rf-req">*</span></label>
            <input
              className={`rf-input ${errors.lastName ? 'rf-input--error' : ''}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Nachname"
              autoComplete="family-name"
            />
          </div>
        </div>

        {isForChild && (
          <div className="rf-field">
            <label className="rf-label">Name des Kindes<span className="rf-req">*</span></label>
            <input
              className={`rf-input ${errors.childName ? 'rf-input--error' : ''}`}
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="Vorname des Kindes"
            />
          </div>
        )}

        <div className="rf-field">
          <label className="rf-label">Handynummer<span className="rf-req">*</span></label>
          <div className="rf-phone">
            <select
              className="rf-select rf-phone__country"
              value={landCode}
              onChange={(e) => setLandCode(e.target.value)}
              aria-label="Land deiner Rufnummer"
            >
              {LAENDER.map((l) => (
                <option key={l.code} value={l.code}>
                  {flagge(l.code)} +{l.vorwahl} {l.name}
                </option>
              ))}
            </select>
            <input
              type="tel"
              className={`rf-input ${errors.phone || telefonUnklar ? 'rf-input--error' : ''}`}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0171 8179758"
              autoComplete="tel"
              inputMode="tel"
            />
          </div>
          {telefonUnklar && (
            <p className="rf-hint rf-hint--warn">
              Diese Nummer sieht noch nicht vollständig aus — bitte kurz prüfen.
            </p>
          )}
        </div>

        <div className="rf-field">
          <label className="rf-label">E-Mail<span className="rf-req">*</span></label>
          <input
            type="email"
            className={`rf-input ${errors.email ? 'rf-input--error' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine E-Mail-Adresse"
            autoComplete="email"
          />
        </div>

        <div className="rf-field">
          <label className="rf-label">Standort wählen<span className="rf-req">*</span></label>
          <select
            className={`rf-select ${errors.school ? 'rf-select--error' : ''}`}
            value={schoolId}
            onChange={(e) => onSchoolChange(e.target.value)}
          >
            <option value="">– Standort auswählen –</option>
            {schools.map((s) => (
              <option key={s.school_id} value={s.school_id}>
                {s.school_name}{s.school_city ? ` · ${s.school_city}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="rf-field">
          <label className="rf-label">Sportart<span className="rf-req">*</span></label>
          {noOffer ? (
            <div className="rf-alert rf-alert--info" style={{ marginBottom: 0 }}>
              {isForChild && ageGroup
                ? `Für ${formatAgeRange(ageGroup.min, ageGroup.max)} gibt es an diesem Standort online gerade keinen buchbaren Kurs.`
                : 'An diesem Standort gibt es online gerade keinen buchbaren Kurs.'}{' '}
              Kein Problem – wir besprechen einen passenden Kurs und einen Termin telefonisch
              mit dir.
            </div>
          ) : onlyCategory ? (
            <>
              <div className="rf-fixed">
                <Check size={16} aria-hidden="true" />
                {onlyCategory.name}
              </div>
              <p className="rf-hint">
                {isForChild && ageGroup
                  ? `Für ${formatAgeRange(ageGroup.min, ageGroup.max)} gibt es an diesem Standort genau eine passende Sportart – sie ist schon ausgewählt.`
                  : 'An diesem Standort gibt es genau eine passende Sportart – sie ist schon ausgewählt.'}
              </p>
            </>
          ) : (
            <select
              className={`rf-select ${errors.category ? 'rf-select--error' : ''}`}
              value={categoryId}
              onChange={(e) => onCategoryChange(e.target.value)}
              disabled={!ready}
            >
              <option value="">
                {!schoolId
                  ? '– zuerst Standort wählen –'
                  : ready
                    ? '– Sportart auswählen –'
                    : '– zuerst Altersgruppe wählen –'}
              </option>
              <option value={UNSURE}>Ich bin mir nicht sicher</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {categoryId === UNSURE && !noOffer && !onlyCategory && (
            <p className="rf-hint">
              Kein Problem – wir besprechen die passende Sportart und einen Termin
              telefonisch mit dir.
            </p>
          )}
          {isForChild && choice?.kind === 'auto' && !askAge && (
            <CoursePick
              option={choice.option}
              alternative={choice.alternative}
              useAlternative={altChosen}
              onPickAlternative={setAltChosen}
            />
          )}
        </div>

        {/* Deckt kein Kurs der Sportart die ganze Altersgruppe ab (z. B. „MMA 9–14"
            bei 12–15), entscheidet das genaue Alter. Dann fragen wir es ab und
            bestimmen den Kurs daraus — statt die Eltern Altersbereiche vergleichen
            zu lassen. Passt zum Alter kein Kurs, bleibt der Rückruf. */}
        {askAge && ageGroup && (
          <div className="rf-field">
            <label className="rf-label" id="rf-exactage-label">
              Wie alt ist dein Kind genau?<span className="rf-req">*</span>
            </label>
            <p className="rf-hint rf-hint--info" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Bei {activeCategoryName} hängt der passende Kurs vom genauen Alter ab. Bitte wähle
              das Alter, das dein Kind <strong>am Tag des Probetrainings</strong> hat.
            </p>
            <div
              className={`rf-agegroups rf-agegroups--exact ${errors.exactAge ? 'rf-agegroups--error' : ''}`}
              role="group"
              aria-labelledby="rf-exactage-label"
            >
              {agesOf(ageGroup).map((age) => (
                <button
                  key={age}
                  type="button"
                  aria-pressed={exactAge === age}
                  className={`btn rf-agebtn ${exactAge === age ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => onExactAgeChange(age)}
                >
                  <span className="rf-agebtn__num">{age}</span>
                  <span className="rf-agebtn__unit">Jahre</span>
                </button>
              ))}
            </div>
            {errors.exactAge && <p className="rf-error">Bitte wähle das genaue Alter deines Kindes.</p>}
            {choice?.kind === 'auto' && (
              <CoursePick
                option={choice.option}
                alternative={choice.alternative}
                useAlternative={altChosen}
                onPickAlternative={setAltChosen}
              />
            )}
            {choice?.kind === 'no-fit' && exactAge !== null && (
              <div className="rf-alert rf-alert--info" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                Für {exactAge} Jahre gibt es bei {activeCategoryName} an diesem Standort keinen
                passenden Kurs. Kein Problem – wir besprechen die passende Sportart und
                einen Termin telefonisch mit dir.
              </div>
            )}
          </div>
        )}

        {/* Erwachsenenkurse für Kinder (und jedes Kind ab 15): Sie sind noch nicht
            volljährig, die Eltern unterschreiben den Vertrag und kommen mit. */}
        {parentsMustCome && (
          <div className="rf-alert rf-alert--info">
            <strong>Bitte kommt gemeinsam:</strong> Weil dein Kind noch nicht volljährig ist,
            müssen die Eltern den Vertrag unterschreiben. Kommt deshalb bitte zusammen zum
            Probetraining.
          </div>
        )}

        <ConsentBlock />

        <div className="rf-field" style={{ marginTop: '1rem' }}>
          <TurnstileWidget onToken={setToken} onExpire={() => setToken('')} />
        </div>

        {submitError && <div className="rf-alert rf-alert--error">{submitError}</div>}

        <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
          {submitting ? <span className="rf-spinner" /> : 'Jetzt Platz sichern'}
        </button>
      </form>
    </>
  );
}
